import {ChangeDetectorRef, Component, OnInit, Optional} from '@angular/core';
import {LayoutService} from '@ngmedax/layout';
import {Translatable, TranslationService} from '@ngmedax/translation';

import {DeviceService} from '../services/device.service';
import {TRANSLATION_GRID_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {Device} from '../../../types';

// hack to inject decorator declarations. must occur before class declaration!
export interface DeviceGridComponent extends Translatable {}

@Component({
  selector: 'app-device-grid',
  templateUrl: './device-grid.component.html',
  styleUrls: ['./device-grid.component.css']
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class DeviceGridComponent implements OnInit {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public devices: Device[] = [];
  public clients: Device.Client[] = [];

  /**
   * Timeout for filter change event
   */
  private filterChangeTimeout: any = null;

  /**
   * Locale for questionnaires. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Search filter
   */
  public filter: Device.Filter = {};

  /**
   * Injects dependencies
   */
  constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    private deviceService: DeviceService,
    private ref: ChangeDetectorRef
  ) {
  }

  /**
   * Loads devices
   */
  public ngOnInit() {
    this.filter = {isBrowser: 0};
    this.loadDeviceList();
    this.deviceService.getClients().then(clients => this.clients = clients.filter(client => !client.firstName.match(/N\/A/)));
  }

  /**
   * Event handler for when paging changes. Triggers the change detection
   */
  public onPagingChange() {
    // trigger change detection
    this.ref.detectChanges();
    this.ref.markForCheck();
  }

  /**
   * Event handler for when filter changes. Loads patients
   */
  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => this.loadDeviceList(), 1000);
  }

  /**
   * Checks if the given position of a patient should be displayed according to the currently selected page
   *
   * @param pos
   * @returns {boolean}
   */
  public isOnCurrentPage(pos) {
    const displayPerPage = this.displayPerPage;
    const maxPos = (displayPerPage * this.gridPageNumber) - 1;
    const minPos = (maxPos - displayPerPage) + 1;
    return pos >= minPos && pos <= maxPos;
  }

  /**
   * Removes device from db
   *
   * @param {Device} device
   */
  public async removeDevice(device: Device) {
    const sure = await confirm(this._(KEYS.GRID.QUESTION_DELETE_DEVICE));

    if (sure) {
      this.deviceService.removeDevice(device).then(() => {
        this.devices.splice(this.devices.indexOf(device), 1);
        alert(this._(KEYS.GRID.SUCCESSFULLY_DELETED_DEVICE));
      }).catch((err) => {
        alert(this._(KEYS.GRID.ERROR_DELETING_DEVICE));
        console.error(err);
      });
    }
  }

  /**
   * Shorts id
   * @param id
   */
  public shortId(id: string) {
    return `(${id.substring(0, 3)}...)`
  }

  /**
   * Loads and shows device list
   */
  private loadDeviceList() {
    this.layoutService.showPreloader();
    const filter = this.getQueryFilter();

    this.deviceService.getDevices(filter).then((devices) => {
      this.devices = <any>devices;
      this.layoutService.hidePreloader();
    }).catch((err) => {
      alert(this._(KEYS.GRID.ERROR_LOADING_DEVICE_LIST));
      this.layoutService.hidePreloader();
      console.error(err);
    });
  }

  /**
   * Returns query filter by filter object
   */
  private getQueryFilter() {
    const filter: any = {'$and': []};
    Object.keys(this.filter).filter(key => key != 'isBrowser' && key != 'client').forEach((key) =>
      filter['$and'].push({[key]: { '$regex' : this.filter[key], '$options' : 'i' }}));

    if (<any>this.filter.isBrowser == 0) {
      filter['$and'].push({deviceName: {'$regex' : '^((?!(Device_[A-Z0-9-])).)*$'}});
    }

    if (this.filter.isBrowser == 1) {
      filter['$and'].push({deviceName: {'$regex' : '^Device_[A-Z0-9-]*$'}});
    }

    if (this.filter.client && this.filter.client.length) {
      filter['$and'].push({client: {'$in' : this.filter.client}});
    }

    console.log(filter);
    return filter['$and'].length ? filter: {};
  }
}
